import type { RoutePreviewSource } from '../RouteSummary/index.ts';

type PreviewState = {
  steps?: readonly (readonly unknown[] | undefined)[];
  segments?: readonly unknown[];
  times?: readonly number[];
  distances?: readonly number[];
  endpoints?: readonly unknown[];
  isRoutePending?: boolean;
  isRouteUnavailable?: boolean;
  isNavigating?: boolean;
};

const sum = (values: readonly number[]) => values.reduce((total, value) => total + value, 0);

/** True when the state holds one coherent, displayable route: every leg has steps, and per-leg data is aligned. */
export const hasPreviewableRoute = (state: PreviewState): boolean => {
  const { steps, segments, times, distances, endpoints, isRouteUnavailable, isRoutePending } = state;
  if (isRoutePending || isRouteUnavailable || !steps?.length || !times || !distances || !endpoints || !segments) {
    return false;
  }
  return (
    steps.every(leg => Boolean(leg?.length)) &&
    segments.length === steps.length &&
    times.length === steps.length &&
    distances.length === steps.length &&
    endpoints.length === steps.length + 1
  );
};

/** The preview replaces the legacy results only while the feature is on, the route is valid and guidance has not started. */
export const isRoutePreviewActive = (state: PreviewState, fullRoutePreviewEnabled: boolean): boolean =>
  fullRoutePreviewEnabled === true && !state.isNavigating && hasPreviewableRoute(state);

/**
 * Adapter input from the selected-route snapshot. Times are minutes and distances meters per leg;
 * the route total is the sum of the per-leg `getRouteDistance` values, not of rounded step rows.
 */
export const getRoutePreviewSource = (state: {
  steps: RoutePreviewSource['steps'];
  times: readonly number[];
  distances: readonly number[];
  endpoints: RoutePreviewSource['endpoints'];
  searchInputs: RoutePreviewSource['searchInputs'];
  preferredUnits?: string;
}): RoutePreviewSource => ({
  steps: state.steps,
  totalTime: sum(state.times),
  totalDistance: sum(state.distances),
  endpoints: state.endpoints,
  searchInputs: state.searchInputs,
  preferredUnits: state.preferredUnits,
});
